import { parseJsonResponse } from '@/utils/safe-json';

/**
 * A non-2xx response from a JSON endpoint. Carries the HTTP `status` so a
 * consumer can separate an access refusal - a 401 (stale session) or the 403
 * our app-level gates answer a JSON request with - from a fault worth
 * reporting. See {@link isReauthStatusError}.
 */
export class HttpStatusError extends Error {
  readonly status: number;

  constructor(status: number, statusText?: string) {
    super(`Request failed with status ${status}${statusText ? ` ${statusText}` : ''}`);
    this.name = 'HttpStatusError';
    this.status = status;
  }
}

/**
 * Whether the error is an access refusal a loader should render as re-auth
 * rather than report: a 401 (stale session, re-authenticated on the next
 * document load) or 403 (a role denial, or an app-level gate that hands a JSON
 * request a machine-readable refusal instead of an HTML redirect it can't
 * follow). Mirrors the 401/403 rule user-context applies to the app-keys fetch.
 */
export const isReauthStatusError = (error: unknown): error is HttpStatusError =>
  error instanceof HttpStatusError && (error.status === 401 || error.status === 403);

/**
 * Whether the error is a benign non-ok status a loader should not report: a 406
 * from a JSON endpoint. A 406 is the server's format-mismatch response
 * (ActionController::UnknownFormat -> head :not_acceptable), which it already
 * excludes from its own Sentry as scanner-probe noise - so capturing it
 * client-side just re-adds that noise. It is not an access refusal, so it is a
 * sibling of {@link isReauthStatusError}, not part of it. Every other non-ok
 * status still reports.
 */
export const isBenignLoaderStatus = (error: unknown): error is HttpStatusError =>
  error instanceof HttpStatusError && error.status === 406;

/**
 * Whether the error is a benign network-layer failure a loader should not
 * report. The browser wording differs (Safari `Load failed`, Chromium `Failed
 * to fetch`, Firefox `NetworkError when attempting to fetch resource`) but all
 * arrive as a statusless `TypeError`, so the status-based guards above can't
 * catch them. The dominant source is an in-flight loader fetch cancelled when
 * the user navigates away - a benign abort with no HTTP response, not a fault.
 */
export const isBenignNetworkError = (error: unknown): error is TypeError =>
  error instanceof TypeError && /load failed|failed to fetch|networkerror/i.test(error.message);

/**
 * Fetches a JSON endpoint. Sets the JSON `Accept` header, then throws
 * {@link HttpStatusError} on any non-ok status *before* reading the body - so a
 * gate's `403 { error, redirect }` is surfaced as a status error rather than
 * parsed into loader data. An ok response is read through {@link parseJsonResponse},
 * so a non-JSON body still becomes a typed `NonJsonResponseError`.
 *
 * Bypasses the HTTP cache because callers fetch the page's own URL, which also
 * serves the HTML document. On a back/forward load Chrome serves the page's
 * fetches from cache without honouring `Vary: Accept`, so a cached document
 * would come back as the loader's data, and cached JSON as the document.
 */
export const fetchJson = async (input: string, init: RequestInit = {}): Promise<unknown> => {
  const res = await fetch(input, {
    cache: 'no-store',
    ...init,
    headers: { Accept: 'application/json', ...init.headers },
  });
  if (!res.ok) throw new HttpStatusError(res.status, res.statusText);
  return parseJsonResponse(res);
};
